import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {Box, Container, Typography, } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import CreateDrawingLocationName from './components/CreateDrawingLocationName';
import { getLocalStorageItem, setLocalStorageItem } from '../../../_helpers';
import axios from 'axios';
import DataGridV2 from '../../../components/DataGridV2';
import { apps, LOCAL_STORAGE_GRID_KEYS } from '../../../_constants';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useSelector } from 'react-redux';
import { INITIAL_ROW_SELECTION_MODEL } from '../../../components/DataGridV2/constants';
import { Add, Delete } from '@mui/icons-material';
import { getDrawingLocationNameColumns, } from './utils';
import { getIdsForGridSelectedRows } from '../../../_helpers/getIdsForGridSelectedRows';

export default function DrawingLocationsNames() {
  const classes = useStyles();
  const apiRef = useGridApiRef();
  const { t , i18n} = useTranslation();
  const app = useSelector((state) => state.app);
  const keyPrefix = app === apps.settingModule ? "SETTING" : "GENERAL";
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRecordIndex, setSelectedRecordIndex] = useState(null);
  const [drawingLocationsNames, setDrawingLocationsNames] = useState([])
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
  const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
     LOCAL_STORAGE_GRID_KEYS.DRAWING_LOCATION_NAMES_PAGINATION[keyPrefix], undefined
  ))
  const [isDialogOpen, setIsDialogOpen] = useState(false); 
  const { webApiUrl } = window.appConfig;
  const [loading, setLoading] = useState({ addDrawingLocationNameLoading: false, getDrawingLocation: false, deleteDrawingLocation: false })
  
  useEffect(() => {
    setLoading(p => ({ ...p, getDrawingLocation: true }))
    const url = webApiUrl + 'api/Dossier/GetDrawingLocationNames';
    axios.get(url)
      .then(response => {
        setDrawingLocationsNames(response.data)
        setLoading(p => ({ ...p, getDrawingLocation: false }))
      })
      .catch(error => {
        setLoading(p => ({ ...p, getDrawingLocation: false }))
      });
  }, [])
 
      const drawingLocationSaveHandler = (data) => {
        const canSendId = selectedRecord ? { id: selectedRecord.id } : {};
        setLoading(p => ({ ...p, addDrawingLocationNameLoading: true }))
        const url = webApiUrl + 'api/Dossier/AddOrUpdateDrawingLocationName';
        axios.post(url, { ...data, ...canSendId })
          .then(({ data: { viewModel } }) => {
            const records = Object.assign([], drawingLocationsNames);
            const updatedData = selectedRecord ? Object.assign(records, { [selectedRecordIndex]: viewModel }) : [...records, viewModel];
            setDrawingLocationsNames(updatedData)
          })
          .catch(error => {
            setLoading(p => ({ ...p, addDrawingLocationNameLoading: false }))
          });
      }

      const forProjectValueOptions = [...new Set(
        drawingLocationsNames
          .filter((item) => item.forProject === true || item.forProject === false)
          .map((item) => item.forProject)
      )].map((value) => ({
        value: value,
        label: value ? "True" : "False"
      }));

      const forBuildingValueOptions = [...new Set( drawingLocationsNames
          .filter((item) => item.forBuilding === true || item.forBuilding === false)
          .map((item) => item.forBuilding)
      )].map((value) => ({
        value: value,
        label: value ? "True" : "False"
      }));

  
  const handleDeleteDrawingLocationNames = async (ids = []) => {
    if (!ids.length) return;

    setLoading((p) => ({ ...p, deleteDrawingLocation: true }));

    const url = webApiUrl + "api/Dossier/DeleteDrawingLocationName/";

    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      try {
        await axios.delete(url + id);
        setDrawingLocationsNames((prev) => prev.filter((l) => l.id !== id));
      } catch (er) {
        console.log(er);
      }
    }

    setLoading((p) => ({ ...p, deleteDrawingLocation: false }));
  };

  const columns = getDrawingLocationNameColumns({t,keyPrefix,i18n,apiRef,forProjectValueOptions,forBuildingValueOptions,setSelectedRecord,setSelectedRecordIndex,drawingLocationsNames,setIsDialogOpen})

  const getToolbarProps = useMemo(() => {
    const drawingLocationsIds = getIdsForGridSelectedRows(rowSelectionModel,drawingLocationsNames);
    return {
      toolbarStartActions: [
        {
          title: t("general.add"),
          icon: Add,
          onClick: () => {
            setSelectedRecord(null);
            setSelectedRecordIndex(null);
            setIsDialogOpen(true);
          },
        },
      ],
      taskMenuListItems: drawingLocationsIds.length > 0
        ? [
          {
            title: t("general.delete"),
            icon: (props) => <Delete {...props} />,
            onClick: () => handleDeleteDrawingLocationNames(drawingLocationsIds),

          },
        ]
        : [],
      title: t('drawing.dossier.library.title')
    };
  }, [drawingLocationsNames, rowSelectionModel, t]);

      const onPaginationModelChange = useCallback((newPaginationModel) => {
        setPaginationModel(newPaginationModel)
        setLocalStorageItem(
          LOCAL_STORAGE_GRID_KEYS.DRAWING_LOCATION_NAMES_PAGINATION[keyPrefix],
          newPaginationModel
        );
      }, [keyPrefix])

  const getDrawingLocationNamesPrintAndDownload = ({ isAllSelected = true, idsToExport = [] }) => {
    const userSelectedRows = isAllSelected? drawingLocationsNames : idsToExport.map(id => drawingLocationsNames.find(item => item?.id === id));
    const results = userSelectedRows?.map((item) => {
      const result = {};
      columns.forEach((column) => {
        const field = column.field;
        let value = item[field]
        if (typeof value === 'boolean') {
          value = value ? 'True' : 'False';
        }
        result[field] = value;
      });
      return result;
    });

    return results;
  };

      return (
        <Container maxWidth={false} elevation={0} className={classes.mainContainer}>
          <Box display="flex" flexDirection="column" sx={{ height: "100%" }}>
          <Box p={2} >
            <Typography className={classes.headerTitle} variant='h6'>{t("drawing.dossier.library.title")}</Typography>
          </Box>
          <Box sx={{flex:1,minHeight:0}}>
          <DataGridV2
            localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.DRAWING_LOCATION_NAMES_SEARCH_TEXT[keyPrefix]}
            localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.DRAWING_LOCATION_NAMES_COLUMN_FILTER[keyPrefix]}
            localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.DRAWING_LOCATION_NAMES_COLUMN_SORT[keyPrefix]}
            localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.DRAWING_LOCATION_NAMES_COLUMN_ORDER[keyPrefix]}
            localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.DRAWING_LOCATION_NAMES_COLUMN_WIDTH[keyPrefix]}
            localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.DRAWING_LOCATION_NAMES_COLUMN_VISIBILITY[keyPrefix]}
            apiRef={apiRef}
            checkboxSelection
            rows={drawingLocationsNames ? drawingLocationsNames : []}
            loading={loading.getDrawingLocation}
            columns={columns}
            pagination
            filterMode="client"
            {...(!!paginationModel && { paginationModel })}
            onPaginationModelChange={onPaginationModelChange}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            toolbarProps={{
              ...getToolbarProps,
              selectedRows: rowSelectionModel,
              getData: getDrawingLocationNamesPrintAndDownload
            }}
          />
          <CreateDrawingLocationName
            state={selectedRecord}
            canOpen={isDialogOpen}
            onSave={(data) => {
              if (data) drawingLocationSaveHandler(data);
              setSelectedRecord(null);
              setSelectedRecordIndex(null);
              setIsDialogOpen(false);
            }}
            onClose={() => {
              setIsDialogOpen(false);
              setSelectedRecord(null);
              setSelectedRecordIndex(null);
            }}
          />
          </Box>
          </Box>
        </Container>
      )
}


const useStyles = makeStyles((theme) => ({
  '@global': {
    '.MuiButtonBase-root:focus': {
      outline: 0,
    },
  },
  mainContainer: {
    height: '100%',
    width: '100%',
    overflow: 'auto',
    padding: theme.spacing(0),
    background: theme.palette.common.white
  },
}))